import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';

const DEMO_BACKEND_URL =
  process.env.REACT_APP_DEMO_BACKEND_URL || 'https://cafe.googlyn.ca';

// All 10 company names for continuous scrolling strip
const ALL_COMPANIES = [
  { name: 'LUMINARY GROUP', style: { fontWeight: 700, fontSize: '0.875rem', letterSpacing: '0.1em' } },
  { name: 'Axiom Digital', style: { fontWeight: 300, fontSize: '1rem', fontStyle: 'italic' } },
  { name: 'Crestview Labs', style: { fontWeight: 500, fontSize: '0.9rem', textTransform: 'capitalize' } },
  { name: 'Nova Collective', style: { fontWeight: 600, fontSize: '0.85rem', letterSpacing: '0.05em' } },
  { name: 'SOLACE STUDIO', style: { fontWeight: 400, fontSize: '0.875rem', letterSpacing: '0.15em' } },
  { name: 'Prisma Ventures', style: { fontWeight: 600, fontSize: '0.9rem', letterSpacing: '0.02em' } },
  { name: 'MERIDIAN CO', style: { fontWeight: 800, fontSize: '0.85rem', letterSpacing: '0.1em' } },
  { name: 'Elara Brands', style: { fontWeight: 400, fontSize: '0.95rem', fontStyle: 'italic' } },
  { name: 'Aurum Agency', style: { fontWeight: 500, fontSize: '0.875rem' } },
  { name: 'ZENITH WORKS', style: { fontWeight: 700, fontSize: '0.9rem', letterSpacing: '0.12em' } },
];

// Customer Logo Strip Component - Continuous Scrolling
const CustomerLogoStrip = () => {
  return (
    <div className="mt-4">
      <p className="text-[10px] text-black/40 uppercase tracking-wider mb-3 text-center">
        Trusted by businesses of all sizes
      </p>
      
      <div className="relative overflow-hidden">
        <div className="flex gap-12 animate-scroll-left">
          {/* First set of company names */}
          {ALL_COMPANIES.map((company, idx) => (
            <div
              key={`set1-${idx}`}
              className="text-black/50 whitespace-nowrap flex-shrink-0"
              style={company.style}
            >
              {company.name}
            </div>
          ))}
          
          {/* Duplicate set for seamless loop */}
          {ALL_COMPANIES.map((company, idx) => (
            <div
              key={`set2-${idx}`}
              className="text-black/50 whitespace-nowrap flex-shrink-0"
              style={company.style}
            >
              {company.name}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const DemoModal = ({ open, onOpenChange, prefillMessage }) => {
  const [loading, setLoading] = useState(false);
  const [consent, setConsent] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitVariant, setSubmitVariant] = useState(null); // 'success' | 'partial' | null
  const [errorMsg, setErrorMsg] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    phone: '',
    phoneCountry: 'CA',
    referral: '',
    message: '',
  });

  /**
   * Normalize a user-entered phone number to E.164.
   *  • Strings starting with '+' are validated to have 8–15 digits after the
   *    '+' and returned digit-cleaned (spaces/dashes/parens removed).
   *  • 10-digit numbers are treated as CA/US and get '+1' prepended.
   *  • 11-digit numbers starting with '1' get '+' prepended.
   *  • Any other input returns null (invalid).
   */
  const normalizePhoneToE164 = (raw) => {
    if (!raw) return null;
    const trimmed = String(raw).trim();
    if (trimmed.startsWith('+')) {
      const digitsOnly = trimmed.slice(1).replace(/\D/g, '');
      if (digitsOnly.length >= 8 && digitsOnly.length <= 15) {
        return `+${digitsOnly}`;
      }
      return null;
    }
    const digitsOnly = trimmed.replace(/\D/g, '');
    if (digitsOnly.length === 10) return `+1${digitsOnly}`;
    if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) return `+${digitsOnly}`;
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!consent) {
      toast.error('Please consent to receive communications to continue');
      return;
    }

    // 1) Phone validation → block submit on invalid input
    const e164Phone = normalizePhoneToE164(formData.phone);
    if (!e164Phone) {
      setPhoneError('Please enter a valid phone number.');
      return;
    }
    setPhoneError('');

    setLoading(true);
    setErrorMsg('');

    // Build the backend payload — combine first + last into `name`, and
    // map the existing "How did you hear about us?" select into
    // `business_type` (closest existing optional field, per product spec).
    const payload = {
      name: `${formData.firstName} ${formData.lastName}`.trim(),
      email: formData.email,
      company: formData.company,
      phone: e164Phone,
      business_type: formData.referral || '',
      message: formData.message || '',
    };

    try {
      const response = await fetch(`${DEMO_BACKEND_URL}/api/demo-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Request failed (${response.status})`);
      }

      const data = await response.json();
      const emailSent = data && data.email_sent === true;
      const callOk = data && data.call_succeeded === true;
      // The lead is captured server-side (DB) regardless of the best-effort
      // call/email notifications — so treat a captured lead as a success.
      const captured = data && (data.success === true || data.lead_saved === true);

      if (!captured) {
        // Lead could not be saved at all → genuine failure.
        setErrorMsg('Something went wrong. Please email us at solutions@googlyn.ca');
      } else if (emailSent && callOk) {
        setSubmitVariant('success');
        setSubmitted(true);
        setConsent(false);
      } else {
        // Lead captured; one/both notifications pending → ops will follow up.
        setSubmitVariant('partial');
        setSubmitted(true);
        setConsent(false);
      }
    } catch (error) {
      // Network / non-2xx → full failure.
      setErrorMsg('Something went wrong. Please email us at solutions@googlyn.ca');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === 'phone') setPhoneError('');
  };

  // When opened with a prefill (e.g. from the AI Employee page teaser),
  // seed the "tell us about your use case" field — including clearing it
  // back to empty when the caller explicitly passes ''. No-op for every
  // other caller of this modal since `prefillMessage` is left undefined there.
  useEffect(() => {
    if (open && prefillMessage !== undefined) {
      setFormData((prev) => ({ ...prev, message: prefillMessage }));
    }
  }, [open, prefillMessage]);

  // Reset transient submission state whenever the modal is closed
  // so the form is fresh on the next open.
  const handleOpenChange = (next) => {
    if (!next) {
      setSubmitted(false);
      setSubmitVariant(null);
      setErrorMsg('');
      setPhoneError('');
      setLoading(false);
    }
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="w-[calc(100vw-1rem)] sm:w-[calc(100vw-2rem)] sm:max-w-7xl max-h-[92vh] overflow-y-auto p-0 bg-white border-black/10"
        data-testid="book-demo-dialog"
        onPointerDownOutside={(e) => {
          // Allow interactions with select dropdown portals
          if (e.target.closest('[role="listbox"]') || e.target.closest('[role="option"]')) {
            e.preventDefault();
          }
        }}
      >
        {/* Accessibility: Hidden title and description for screen readers */}
        <DialogTitle className="sr-only">Book a Demo - GoogLyn AI</DialogTitle>
        <DialogDescription className="sr-only">
          Schedule a demonstration of GoogLyn AI's elegant intelligence platform. Fill out the form to connect with our team.
        </DialogDescription>
        
        <div className="grid grid-cols-1 lg:grid-cols-2">
          {/* Left Side - Form */}
          <div className="p-4 sm:p-6 lg:p-8">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-light text-black/90 mb-1 sm:mb-2">
              Book a demo
            </h2>
            <p className="text-xs sm:text-sm text-black/60 mb-4 sm:mb-5">
              Fill out the form and our team will be in touch shortly.
            </p>

            {submitted ? (
              <div
                className="space-y-6"
                role="status"
                aria-live="polite"
                data-testid="demo-success-message"
              >
                <p className="text-2xl md:text-3xl font-light text-black/90 leading-relaxed">
                  {submitVariant === 'partial'
                    ? 'Your request was received. Our team will follow up with you shortly.'
                    : "Thanks — we'll reach out shortly."}
                </p>
              </div>
            ) : (
            <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
              {/* First name & Last name */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="firstName" className="text-black/80 text-xs">
                    First name *
                  </Label>
                  <Input
                    id="firstName"
                    placeholder="First name"
                    value={formData.firstName}
                    onChange={(e) => handleChange('firstName', e.target.value)}
                    required
                    className="h-9 bg-[#F5F5F5] border-0 text-black placeholder:text-black/40"
                    data-testid="demo-firstname-input"
                  />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="lastName" className="text-black/80 text-xs">
                    Last name *
                  </Label>
                  <Input
                    id="lastName"
                    placeholder="Last name"
                    value={formData.lastName}
                    onChange={(e) => handleChange('lastName', e.target.value)}
                    required
                    className="h-9 bg-[#F5F5F5] border-0 text-black placeholder:text-black/40"
                    data-testid="demo-lastname-input"
                  />
                </div>
              </div>

              {/* How did you hear & Company name */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="referral" className="text-black/80 text-xs">
                    How did you hear about us?
                  </Label>
                  <Select value={formData.referral} onValueChange={(value) => handleChange('referral', value)}>
                    <SelectTrigger className="h-9 bg-[#F5F5F5] border-0 text-black" data-testid="demo-referral-select">
                      <SelectValue placeholder="Select an option..." />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-black/10">
                      <SelectItem value="search">Search engine</SelectItem>
                      <SelectItem value="social">Social media</SelectItem>
                      <SelectItem value="referral">Word of mouth</SelectItem>
                      <SelectItem value="press">Press / Article</SelectItem>
                      <SelectItem value="event">Event / Conference</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1">
                  <Label htmlFor="company" className="text-black/80 text-xs">
                    Company name *
                  </Label>
                  <Input
                    id="company"
                    placeholder="Company name"
                    value={formData.company}
                    onChange={(e) => handleChange('company', e.target.value)}
                    required
                    className="h-9 bg-[#F5F5F5] border-0 text-black placeholder:text-black/40"
                    data-testid="demo-company-input"
                  />
                </div>
              </div>

              {/* Work email & Phone number */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="email" className="text-black/80 text-xs">
                    Work email *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Work email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    required
                    className="h-9 bg-[#F5F5F5] border-0 text-black placeholder:text-black/40"
                    data-testid="demo-email-input"
                  />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="phone" className="text-black/80 text-xs">
                    Phone number *
                  </Label>
                  <div className="flex gap-2">
                    <Select value={formData.phoneCountry} onValueChange={(value) => handleChange('phoneCountry', value)}>
                      <SelectTrigger className="w-20 h-9 bg-[#F5F5F5] border-0">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-black/10">
                        <SelectItem value="US">US</SelectItem>
                        <SelectItem value="UK">UK</SelectItem>
                        <SelectItem value="CA">CA</SelectItem>
                        <SelectItem value="AU">AU</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="+1"
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      required
                      className={`flex-1 h-9 bg-[#F5F5F5] border-0 text-black placeholder:text-black/40 ${
                        phoneError ? 'ring-1 ring-red-500' : ''
                      }`}
                      data-testid="demo-phone-input"
                      aria-invalid={phoneError ? 'true' : 'false'}
                      aria-describedby={phoneError ? 'demo-phone-error' : undefined}
                    />
                  </div>
                  {phoneError && (
                    <p
                      id="demo-phone-error"
                      className="text-[11px] text-red-600 mt-1"
                      data-testid="demo-phone-error"
                    >
                      {phoneError}
                    </p>
                  )}
                </div>
              </div>

              {/* Message */}
              <div className="space-y-1">
                <Label htmlFor="message" className="text-black/80 text-xs">
                  Please tell us more about your use case and how we can best help you
                </Label>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => handleChange('message', e.target.value)}
                  placeholder="Type your message..."
                  rows={2}
                  className="bg-[#F5F5F5] border-0 text-black placeholder:text-black/40 resize-none text-sm"
                  data-testid="demo-message-input"
                />
              </div>

              {/* Consent */}
              <div className="flex items-start space-x-2">
                <Checkbox
                  id="consent"
                  checked={consent}
                  onCheckedChange={setConsent}
                  className="mt-1 cursor-pointer"
                  style={{ pointerEvents: 'auto' }}
                  data-testid="demo-consent-checkbox"
                />
                <label
                  htmlFor="consent"
                  className="text-xs text-black/60 leading-snug cursor-pointer"
                  style={{ pointerEvents: 'auto' }}
                >
                  I consent to receive operational and marketing communications from GoogLyn AI via call, text, or email. Message and data rates may apply. Message frequency may vary. Reply STOP to opt out at any time. *
                </label>
              </div>

              {/* Inline error message — preserves form data on failure */}
              {errorMsg && (
                <div
                  role="alert"
                  className="text-sm text-[#B45369] bg-[#FDF1F4] border border-[#F4C2D4] rounded-md px-4 py-3"
                  data-testid="demo-error-message"
                >
                  {errorMsg}
                </div>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#3D2D1F] text-white hover:bg-[#2B1F15] transition-all text-sm py-3"
                style={{ pointerEvents: 'auto' }}
                data-testid="book-demo-submit-button"
              >
                {loading ? 'Sending...' : 'Book a Demo'}
              </Button>
            </form>
            )}
          </div>

          {/* Right Side - Visual Content */}
          <div className="relative bg-gradient-to-br from-[#E8E4DC] to-[#D4CEC4] p-4 sm:p-6 lg:p-8 flex flex-col justify-between">
            <div>
              <h3 className="text-2xl sm:text-3xl md:text-4xl font-light text-black/90 mb-2 sm:mb-3 leading-tight">
                See GoogLyn
                <br />
                in Action
              </h3>
              <p className="text-black/60 text-sm sm:text-base mb-4 sm:mb-5">
                Learn how GoogLyn AI can work for you.
              </p>

              {/* Branded GoogLyn AI visual — midnight navy backdrop with
                  concentric champagne-gold rings, subtle radial glow, and
                  the primary GoogLyn AI logo + wordmark centered. Replaces
                  the previous stock photo. Purely decorative → aria-hidden. */}
              <div
                className="rounded-lg overflow-hidden mb-4 sm:mb-5 relative h-40 sm:h-44 lg:h-48 flex items-center justify-center"
                style={{ backgroundColor: '#141e30' }}
                data-testid="demo-branded-visual"
                aria-hidden="true"
              >
                {/* Radial gold glow accents (soft, low-opacity) */}
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      'radial-gradient(circle at 70% 30%, rgba(212,175,55,0.10), transparent 55%), radial-gradient(circle at 25% 75%, rgba(212,175,55,0.08), transparent 60%)',
                  }}
                />
                {/* Concentric champagne-gold rings */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-44 h-44 rounded-full border border-[#D4AF37]/10 absolute" />
                  <div className="w-32 h-32 rounded-full border border-[#D4AF37]/15 absolute" />
                  <div className="w-24 h-24 rounded-full border border-[#D4AF37]/20 absolute" />
                </div>
                {/* Foreground brand mark */}
                <div className="relative z-10 flex flex-col items-center gap-2 px-6 text-center">
                  <img
                    src="https://customer-assets.emergentagent.com/job_googlyn-ai/artifacts/yba1j97l_P3.png"
                    alt=""
                    className="h-10 w-10 rounded-sm opacity-95"
                    style={{ aspectRatio: '1/1' }}
                  />
                  <div className="text-white text-lg font-medium tracking-wide">
                    GoogLyn AI
                  </div>
                  <div
                    className="text-[10px] uppercase tracking-[0.28em]"
                    style={{ color: '#D4AF37' }}
                  >
                    Elegant Intelligence
                  </div>
                </div>
              </div>
            </div>

            {/* Customer Logo Strip */}
            <CustomerLogoStrip />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DemoModal;
